import Iframe from "./Iframe.svelte";
import { mergeAttributes, Node, nodeInputRule, nodePasteRule } from "@tiptap/core";
import { Alignment, Size } from "$components/editor/extensions/blocks/shared/attributes";
import { SvelteNodeViewRenderer } from "svelte-tiptap";
import {
  EmbedProvider,
  isValidFigmaURL,
  isValidGraphyURL,
  isValidLoomURL,
  isValidVimeoURL,
  isValidYoutubeURL,
  isValidCodeSandboxURL,
  isValidRiveURL,
} from "./utils";
import { type MarkdownSerializerState } from "prosemirror-markdown";
import { type Node as PMNode } from "@tiptap/pm/model";

export interface EmbedOptions {
  inline: boolean;
  width: number | string;
  height: number | string;
  controls: boolean;
  nocookie: boolean;
  allowFullscreen: boolean;
  HTMLAttributes: Record<string, any>;
  autoplay: boolean;
  type: EmbedProvider;
  init: boolean;
}

type SetEmbed<ReturnType> = (options: {
  src: string;
  width?: number | string;
  height?: number | string;
  start?: number;
}) => ReturnType;

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    youtube: {
      /**
       * Insert a youtube video
       */
      setYoutubeVideo: SetEmbed<ReturnType>;
      setLoomVideo: SetEmbed<ReturnType>;
      setFigmaBlock: SetEmbed<ReturnType>;
      setVimeoBlock: SetEmbed<ReturnType>;
      setGraphyBlock: SetEmbed<ReturnType>;
      setCodeSandboxBlock: SetEmbed<ReturnType>;
      setRiveBlock: SetEmbed<ReturnType>;
    };
  }
}

export const IframeEmbed = Node.create<EmbedOptions>({
  name: "iframe-embed",

  addOptions() {
    return {
      inline: false,
      controls: true,
      HTMLAttributes: {},
      nocookie: false,
      allowFullscreen: false,
      width: Size.MEDIUM,
      height: "100%",
      autoplay: false,
      type: EmbedProvider.youtube,
      init: false,
      match: [],
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? "inline" : "block";
  },

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      start: {
        default: 0,
      },
      width: {
        default: this.options.width,
      },
      height: {
        default: this.options.height,
      },
      align: {
        default: Alignment.LEFT,
      },
      autoplay: {
        default: this.options.autoplay,
      },
      type: {
        default: EmbedProvider.youtube,
      },
      controls: {
        default: this.options.controls,
      },
      init: {
        default: this.options.init,
      },
      match: {
        default: [],
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: "div[data-iframe-embed] iframe",
      },
    ];
  },

  addCommands() {
    return {
      setYoutubeVideo:
        (options) =>
        ({ commands }) => {
          if (!isValidYoutubeURL(options.src)) {
            return false;
          }

          return commands.insertContent({
            type: this.name,
            attrs: { ...options, type: EmbedProvider.youtube },
          });
        },
      setLoomVideo:
        (options) =>
        ({ commands }) => {
          if (!isValidLoomURL(options.src)) {
            return false;
          }

          return commands.insertContent({
            type: this.name,
            attrs: { ...options, type: EmbedProvider.loom },
          });
        },
      setFigmaBlock:
        (options) =>
        ({ commands }) => {
          if (!isValidFigmaURL(options.src)) {
            return false;
          }

          return commands.insertContent({
            type: this.name,
            attrs: { ...options, type: EmbedProvider.figma },
          });
        },
      setVimeoBlock:
        (options) =>
        ({ commands }) => {
          if (!isValidVimeoURL(options.src)) {
            return false;
          }

          return commands.insertContent({
            type: this.name,
            attrs: { ...options, type: EmbedProvider.vimeo },
          });
        },
      setGraphyBlock:
        (options) =>
        ({ commands }) => {
          if (!isValidGraphyURL(options.src)) {
            return false;
          }

          return commands.insertContent({
            type: this.name,
            attrs: { ...options, type: EmbedProvider.graphy },
          });
        },
      setCodeSandboxBlock:
        (options) =>
        ({ commands }) => {
          if (!isValidCodeSandboxURL(options.src)) {
            return false;
          }
          return commands.insertContent({
            type: this.name,
            attrs: { ...options, type: EmbedProvider.codesandbox },
          });
        },
      setRiveBlock:
        (options) =>
        ({ commands }) => {
          if (!isValidRiveURL(options.src)) {
            return false;
          }
          return commands.insertContent({
            type: this.name,
            attrs: { ...options, type: EmbedProvider.rive },
          });
        },
    };
  },
  addNodeView() {
    return SvelteNodeViewRenderer(Iframe);
  },
  renderHTML({ HTMLAttributes }) {
    return ["iframe", mergeAttributes(HTMLAttributes)];
  },
  // renderHTML({ HTMLAttributes }) {
  //   console.log("HI", HTMLAttributes)
  //   if (HTMLAttributes.type == EmbedProvider.loom) {
  //     console.log("figma");
  //     HTMLAttributes.src = `https://www.figma.com/embed?embed_host=outverse&url=${HTMLAttributes.src}`
  //   }
  //   if (HTMLAttributes.type == EmbedProvider.figma) {
  //     console.log("figma");
  //     HTMLAttributes.src = `https://www.figma.com/embed?embed_host=outverse&url=${HTMLAttributes.src}`
  //   } else {

  //     const embedUrl = getEmbedURLFromYoutubeURL({
  //       url: HTMLAttributes.src,
  //       controls: HTMLAttributes.controls,
  //       nocookie: HTMLAttributes.nocookie,
  //       startAt: HTMLAttributes.start || 0,
  //       autoplay: HTMLAttributes.autoplay,
  //     })

  //     HTMLAttributes.src = embedUrl
  //   }
  //   let animate = {};
  //   if (HTMLAttributes.init) {
  //     animate = {
  //       style:" visibility:hidden;",
  //       onload: "this.style.visibility = 'visible'; this.className='rounded  animate-pop';",}
  //   }
  //   HTMLAttributes.src = "https://open.spotify.com/embed/episode/7makk4oTQel546B0PZlDM5"
  //   return [
  //     'div',
  //     { 'data-youtube-video': '' },
  //     [
  //       'iframe',
  //       mergeAttributes(
  //         this.options.HTMLAttributes,
  //         {
  //           width: this.options.width,
  //           height: this.options.height,
  //           allowfullscreen: this.options.allowFullscreen,
  //           class: "rounded",
  //           ...animate,
  //         },
  //         HTMLAttributes,
  //       ),
  //     ],
  //   ]
  // },

  addInputRules() {
    return [
      nodeInputRule({
        find: /^(https?:\/\/)?(www\.|music\.)?(youtube\.com|youtu\.be)(.+)?/,
        type: this.type,
        getAttributes: (match) => {
          return { src: match[0] };
        },
      }),
      nodeInputRule({
        find: /^(https?:\/\/)?(graphy\.app)\/(workspace\/)?(.+)?$/g,
        type: this.type,
        getAttributes: (match) => {
          return { src: match[0] };
        },
      }),
    ];
  },

  addPasteRules() {
    return [
      nodePasteRule({
        find: /^(https?:\/\/)?(www\.|music\.)?(youtube\.com|youtu\.be)(.+)?$/g,
        type: this.type,
        getAttributes: (match) => {
          return { match, src: match[0], autoplay: false, init: true };
        },
      }),
      nodePasteRule({
        find: /https:\/\/([\w.-]+\.)?figma.com\/(file|proto)\/([0-9a-zA-Z]{22,128})(?:\/.*)?$/g,
        type: this.type,
        getAttributes: (match) => {
          return {
            match,
            src: match[0],
            autoplay: false,
            type: EmbedProvider.figma,
            init: true,
          };
        },
      }),
      nodePasteRule({
        find: /https:\/\/([\w.-]+\.)?loom.com\/(embed|share)\/([0-9a-zA-Z]{22,128})(?:\/.*)?$/g,
        type: this.type,
        getAttributes: (match) => {
          return {
            match,
            src: match[0],
            autoplay: false,
            type: EmbedProvider.loom,
            init: true,
          };
        },
      }),
      nodePasteRule({
        find: /^(https?:\/\/)?(player\.)?(vimeo\.com)\/(video\/)?(.+)?$/g,
        type: this.type,
        getAttributes: (match) => {
          return {
            match,
            src: match[0],
            autoplay: false,
            type: EmbedProvider.vimeo,
            init: true,
          };
        },
      }),
      nodePasteRule({
        find: /^(https?:\/\/)?(share\.graphy\.app)\/(workspace\/)?(.+)?$/g,
        type: this.type,
        getAttributes: (match) => {
          return {
            match,
            src: match[0],
            autoplay: false,
            type: EmbedProvider.graphy,
            init: true,
          };
        },
      }),
      //https://codesandbox.io/embed/qqkn52?view=preview&module=%2Fpublic%2Findex.html
      nodePasteRule({
        find: /^(https?:\/\/)?(codesandbox\.io)\/(embed\/)?(.+)?$/g,
        type: this.type,
        getAttributes: (match) => {
          return {
            match,
            src: match[0],
            autoplay: false,
            type: EmbedProvider.codesandbox,
            init: true,
          };
        },
      }),
      // Rive
      // https://rive.app/s/VZvztJp4JUe_aUaQznH_FA/embed
      // https://rive.app/community/4156-8588-404-land/
      nodePasteRule({
        find: /^https?:\/\/?rive\.app\/(s|community)\/?(.+)?\/embed$/g,
        type: this.type,
        getAttributes: (match) => {
          return {
            match,
            src: match[0],
            autoplay: false,
            type: EmbedProvider.rive,
            init: true,
          };
        },
      }),
    ];
  },

  addStorage() {
    return {
      markdown: {
        serialize: (state: MarkdownSerializerState, node: PMNode) => {
          const src = node.attrs.src;
          const type = node.attrs.type.charAt(0).toUpperCase() + node.attrs.type.slice(1);
          state.write(`[${type}](${src})\n\n`);
        },
      },
    };
  },
});
